// Bible API Service
// Primary source: official YouVersion Platform API (licensed text incl.
// NIV) for versions it carries. Falls back to bible-api.com (public domain
// translations), and finally to KJV, if YouVersion is unavailable or
// doesn't carry the requested version.
import { fetchYouVersionVerse, YOUVERSION_ABBREVIATIONS } from './youVersionApi';

// Map of Bible version codes to bible-api.com translation identifiers.
const NATIVE_VERSIONS = {
  'KJV': 'kjv',      // King James Version
  'ASV': 'asv',      // American Standard Version
  'WEB': 'web',       // World English Bible
  'BBE': 'bbe',      // Bible in Basic English
  'DARBY': 'darby',  // Darby Translation
  'YLT': 'ylt',      // Young's Literal Translation
};

// Versions not carried by bible-api.com but licensed on our YouVersion key;
// requesting these uses YouVersion, falling back to KJV only if that fails.
export const FALLBACK_ONLY_VERSIONS = ['NIV', 'NIVUK', 'NASB', 'AMP', 'TPT'];

function cleanText(text) {
  return text.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
}

async function fetchFromBibleApi(reference, apiVersion) {
  const apiUrl = `https://bible-api.com/${encodeURIComponent(reference)}?translation=${apiVersion}`;
  const response = await fetch(apiUrl);
  if (!response.ok) {
    throw new Error(`API returned ${response.status}`);
  }
  const data = await response.json();
  if (!data.text) {
    throw new Error('No text returned from API');
  }
  // When a passage spans multiple verses, prefix each with its verse number
  // so a staged range reads "5 ... 6 ... 7 ..." rather than one flat blob.
  let text;
  if (Array.isArray(data.verses) && data.verses.length > 1) {
    text = data.verses
      .map((v) => `${v.verse} ${cleanText(v.text)}`)
      .join('  ');
  } else {
    text = cleanText(data.text);
  }
  return { text, reference: data.reference || reference };
}

/**
 * Fetch a Bible verse.
 * @param {string} reference - Bible reference (e.g., "John 3:16")
 * @param {string} version - Bible version code (e.g., "KJV")
 * @returns {Promise<object>} - { success, text, version, fallback }
 */
export const fetchBibleVerse = async (reference, version = 'KJV') => {
  const isNativeVersion = NATIVE_VERSIONS.hasOwnProperty(version);

  // Try YouVersion first for any version it's mapped to (this includes
  // some versions also carried by bible-api.com, e.g. KJV — YouVersion's
  // licensed text is preferred when available).
  if (YOUVERSION_ABBREVIATIONS[version]) {
    const yvResult = await fetchYouVersionVerse(reference, version);
    if (yvResult.success) {
      return { success: true, text: yvResult.text, version, reference: yvResult.reference, fallback: false, source: 'youversion' };
    }
  }

  const apiVersion = isNativeVersion ? NATIVE_VERSIONS[version] : 'kjv';

  try {
    const { text, reference: resolvedReference } = await fetchFromBibleApi(reference, apiVersion);
    return isNativeVersion
      ? { success: true, text, version, reference: resolvedReference, fallback: false }
      : { success: true, text, version: 'KJV', requestedVersion: version, reference: resolvedReference, fallback: true };
  } catch (error) {
    console.error('Bible API Error:', error);

    // If the original request wasn't already the KJV fallback, retry with it.
    if (apiVersion !== 'kjv') {
      try {
        const { text, reference: resolvedReference } = await fetchFromBibleApi(reference, 'kjv');
        return { success: true, text, version: 'KJV', requestedVersion: version, reference: resolvedReference, fallback: true };
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    }

    return {
      success: false,
      text: `Error loading ${reference}: ${error.message}`,
      version,
      fallback: false
    };
  }
};

/**
 * Get list of all supported Bible versions.
 * @returns {string[]}
 */
export const getAvailableVersions = () => {
  return [...Object.keys(NATIVE_VERSIONS), ...FALLBACK_ONLY_VERSIONS];
};

/**
 * Whether a version is natively available from bible-api.com (vs. a KJV
 * fallback substitution when YouVersion is also unavailable).
 * @param {string} version - Version code
 * @returns {boolean}
 */
export const isNativeVersion = (version) => {
  return NATIVE_VERSIONS.hasOwnProperty(version);
};

const bibleApi = {
  fetchBibleVerse,
  getAvailableVersions,
  isNativeVersion,
  FALLBACK_ONLY_VERSIONS
};

export default bibleApi;
